import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Home, Search, Inbox, Plus, Settings, LogOut, Moon, Sun, User, Layers, CalendarDays, Gift, Globe, ClipboardCheck, Crown, ShieldCheck } from "lucide-react";
import { useTheme } from "next-themes";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, SpaceAvatar } from "@/components/Avatars";
import { NuxxumLogo, NuxxumWordmark } from "@/components/NuxxumLogo";
import { CreateSpaceDialog } from "@/components/CreateSpaceDialog";
import { CommandPalette } from "@/components/CommandPalette";
import { AiAssistant } from "@/components/AiAssistant";
import { getEffectiveLang } from "@/lib/i18n";
import { ENVIRONMENTS, ENV_ORDER, envKeyOf, tint } from "@/lib/environments";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavItem = ({ to, icon: Icon, label, badge, active, onClick, testId }) => {
  const cls = `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
    active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
  }`;
  const content = (
    <>
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">{badge}</span>
      )}
    </>
  );
  return to ? (
    <Link to={to} className={cls} data-testid={testId}>{content}</Link>
  ) : (
    <button onClick={onClick} className={`${cls} w-full text-left`} data-testid={testId}>{content}</button>
  );
};

const MOBILE_NAV = [
  { to: "/", icon: Home, label: "Inicio", testId: "mobile-nav-home" },
  { to: "/calendar", icon: CalendarDays, label: "Agenda", testId: "mobile-nav-calendar" },
  { icon: Search, label: "Buscar", testId: "mobile-nav-search" },
  { to: "/spaces", icon: Layers, label: "Espacios", testId: "mobile-nav-spaces" },
  { to: "/profile", icon: Settings, label: "Perfil", testId: "mobile-nav-profile" },
];

const MobileBottomNav = ({ pathname, onSearch }) => (
  <nav className="md:hidden print:!hidden fixed bottom-0 inset-x-0 h-16 glass-sidebar bg-[#FBF6EC]/90 dark:bg-[#0B0E14]/80 border-t border-border/60 z-30 grid grid-cols-5">
    {MOBILE_NAV.map((item) =>
      item.to ? (
        <Link key={item.label} to={item.to} data-testid={item.testId}
          className={`flex flex-col items-center justify-center gap-1 text-xs ${pathname === item.to ? "text-primary" : "text-muted-foreground"}`}>
          <item.icon className="h-5 w-5" />
          {item.label}
        </Link>
      ) : (
        <button key={item.label} onClick={onSearch} data-testid={item.testId}
          className="flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground">
          <item.icon className="h-5 w-5" />
          {item.label}
        </button>
      )
    )}
  </nav>
);

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const { data: home } = useQuery({
    queryKey: ["home"],
    queryFn: async () => (await api.get("/home")).data,
  });
  const { data: notifCount } = useQuery({
    queryKey: ["notif-count"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data,
    refetchInterval: 15000,
  });
  const workspaces = home?.workspaces || [];
  const inboxCount = (home?.pending_invitations?.length || 0) + (notifCount?.count || 0);
  const spaceMatch = location.pathname.match(/^\/space\/([^/]+)/);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  const lang = getEffectiveLang(user);
  const toggleLang = async () => {
    const next = lang === "es" ? "en" : "es";
    try {
      await api.put("/profile", { language: next });
      window.location.reload();
    } catch (error) {
      console.error("Error al cambiar de idioma:", error);
      toast.error(lang === "es" ? "No se pudo cambiar el idioma" : "Could not change language");
    }
  };

  const langButton = (
    <button
      data-testid="language-toggle-btn"
      onClick={toggleLang}
      title={lang === "es" ? "Switch to English" : "Cambiar a Castellano"}
      className="flex items-center gap-1 px-2 h-7 rounded-md text-xs font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors border border-border/60"
    >
      <Globe className="h-3.5 w-3.5" />
      {lang.toUpperCase()}
    </button>
  );

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary">
          <UserAvatar name={user?.name} color={user?.avatar_color} src={user?.avatar_url} size="md" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <p className="text-sm font-semibold">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
          <User className="h-4 w-4 mr-2" /> Mi perfil
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-subscription" onClick={() => navigate("/subscription")}>
          <Crown className="h-4 w-4 mr-2" /> Suscripción
        </DropdownMenuItem>
        {user?.is_admin && (
          <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
            <ShieldCheck className="h-4 w-4 mr-2" /> Administración
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="menu-theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
          {theme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
          {theme === "dark" ? "Modo claro" : "Modo oscuro"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={handleLogout} className="text-destructive">
          <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex print:!hidden fixed inset-y-0 left-0 w-64 flex-col glass-sidebar bg-[#FBF6EC]/85 dark:bg-[#0B0E14]/60 border-r border-border/60 z-30">
        <div className="flex items-center gap-2 px-5 h-16">
          <NuxxumLogo size={30} />
          <NuxxumWordmark className="text-lg" />
          <div className="flex-1" />
          {langButton}
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto pb-4">
          <NavItem to="/" icon={Home} label="Inicio" active={location.pathname === "/"} testId="nav-home" />
          <NavItem to="/calendar" icon={CalendarDays} label="Calendario" active={location.pathname === "/calendar"} testId="nav-calendar" />
          <NavItem to="/my-tasks" icon={ClipboardCheck} label="Tareas asignadas a mí" active={location.pathname === "/my-tasks"} testId="nav-my-tasks" />
          <NavItem to="/wishlist" icon={Gift} label="Mis WishList" active={location.pathname === "/wishlist"} testId="nav-wishlist" />
          <NavItem icon={Search} label="Buscar" onClick={() => setPaletteOpen(true)} testId="nav-search" />
          <NavItem to="/inbox" icon={Inbox} label="Bandeja de entrada" badge={inboxCount} active={location.pathname === "/inbox"} testId="nav-inbox" />
          <div className="pt-5 pb-1 px-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Espacios</span>
            <button
              data-testid="sidebar-create-space-btn"
              onClick={() => setCreateOpen(true)}
              className="h-6 w-6 rounded-md hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {ENV_ORDER
            .map((key) => ({ key, env: ENVIRONMENTS[key], items: workspaces.filter((w) => envKeyOf(w) === key) }))
            .filter((g) => g.items.length > 0)
            .map(({ key, env, items }) => (
              <div key={key} className="pt-1">
                <Link
                  to={`/spaces?env=${key}`}
                  data-testid={`sidebar-env-group-${key}`}
                  className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors hover:opacity-80"
                  style={{ color: env.text }}
                >
                  <span aria-hidden>{env.emoji}</span> {env.label}
                </Link>
                {items.map((ws) => (
                  <Link
                    key={ws.id}
                    to={`/space/${ws.id}`}
                    data-testid={`sidebar-space-${ws.id}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith(`/space/${ws.id}`)
                        ? "text-foreground"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}
                    style={location.pathname.startsWith(`/space/${ws.id}`) ? { backgroundColor: tint(env.color, 0.18) } : undefined}
                  >
                    <SpaceAvatar icon={ws.icon} color={ws.color} size="sm" />
                    <span className="truncate">{ws.name}</span>
                  </Link>
                ))}
              </div>
            ))}
          {workspaces.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Aún no tienes Espacios</p>
          )}
        </nav>
        <div className="border-t border-border/60 p-3 flex items-center gap-3">
          {userMenu}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
      </aside>

      {/* Topbar mobile */}
      <header className="md:hidden print:!hidden fixed top-0 inset-x-0 h-14 glass-sidebar bg-[#FBF6EC]/85 dark:bg-[#0B0E14]/70 border-b border-border/60 z-30 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <NuxxumLogo size={26} />
          <NuxxumWordmark />
        </Link>
        <div className="flex items-center gap-3">
          {langButton}
          <Link to="/inbox" className="relative" data-testid="mobile-inbox-btn">
            <Inbox className="h-5 w-5 text-muted-foreground" />
            {inboxCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[9px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                {inboxCount}
              </span>
            )}
          </Link>
          {userMenu}
        </div>
      </header>

      {/* Main content */}
      <main className="md:pl-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen print:!pl-0 print:!pt-0 print:!pb-0">
        {children}
      </main>

      {/* Bottom nav mobile */}
      <MobileBottomNav pathname={location.pathname} onSearch={() => setPaletteOpen(true)} />

      {/* FAB mobile */}
      <Button
        data-testid="fab-create-space"
        onClick={() => setCreateOpen(true)}
        className="md:hidden print:!hidden fixed bottom-20 right-4 h-14 w-14 rounded-2xl shadow-lg z-30 p-0"
      >
        <Plus className="h-6 w-6" />
      </Button>

      {spaceMatch && <AiAssistant key={spaceMatch[1]} workspaceId={spaceMatch[1]} />}

      <CreateSpaceDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} workspaces={workspaces} onCreateSpace={() => setCreateOpen(true)} />
    </div>
  );
}
